/**
 * The languages Stronger Memory ships, and how a device or browser preference maps onto
 * them. Platform-free: web, mobile and desktop each call `resolveLocale` with their own
 * preference list (URL, stored choice, `navigator.languages`, `expo-localization`).
 */
export const SHIPPED_LOCALES = [
  'en-US',
  'en-GB',
  'es',
  'pt-BR',
  'zh-Hans',
  'zh-Hant',
  'ja',
  'ko',
] as const;
export type ShippedLocale = (typeof SHIPPED_LOCALES)[number];

/**
 * Pseudo-locale for troubleshooting: every translated string comes back accented, ~35%
 * longer and wrapped in ⟦ ⟧, so hard-coded text (stays plain) and clipped layouts (missing
 * closing bracket) stand out. `en-XA` is the tag Android and Chrome use for the same idea.
 * Only offered in non-production builds.
 */
export const PSEUDO_LOCALE = 'en-XA' as const;
export type PseudoLocale = typeof PSEUDO_LOCALE;

export type AppLocale = ShippedLocale | PseudoLocale;
export const ALL_LOCALES: readonly AppLocale[] = [...SHIPPED_LOCALES, PSEUDO_LOCALE];

/** en-US is the source language: message ids are its text and every other pack falls back to it. */
export const DEFAULT_LOCALE: ShippedLocale = 'en-US';

export interface LocaleInfo {
  tag: AppLocale;
  /** Shown in language pickers — always in its own language, deliberately never translated. */
  nativeName: string;
  /** For logs and analytics. */
  englishName: string;
  shipped: boolean;
}

export const LOCALE_INFO: Readonly<Record<AppLocale, LocaleInfo>> = {
  'en-US': {
    tag: 'en-US',
    nativeName: 'English (US)',
    englishName: 'English (United States)',
    shipped: true,
  },
  'en-GB': {
    tag: 'en-GB',
    nativeName: 'English (UK)',
    englishName: 'English (United Kingdom)',
    shipped: true,
  },
  es: { tag: 'es', nativeName: 'Español', englishName: 'Spanish', shipped: true },
  'pt-BR': {
    tag: 'pt-BR',
    nativeName: 'Português (Brasil)',
    englishName: 'Portuguese (Brazil)',
    shipped: true,
  },
  'zh-Hans': {
    tag: 'zh-Hans',
    nativeName: '简体中文',
    englishName: 'Chinese (Simplified)',
    shipped: true,
  },
  'zh-Hant': {
    tag: 'zh-Hant',
    nativeName: '繁體中文',
    englishName: 'Chinese (Traditional)',
    shipped: true,
  },
  ja: { tag: 'ja', nativeName: '日本語', englishName: 'Japanese', shipped: true },
  ko: { tag: 'ko', nativeName: '한국어', englishName: 'Korean', shipped: true },
  'en-XA': {
    tag: 'en-XA',
    nativeName: 'Pseudo (troubleshooting)',
    englishName: 'Pseudo-locale',
    shipped: false,
  },
};

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (ALL_LOCALES as readonly string[]).includes(value);
}

/** Regions whose English uses British spelling and day-first dates; every other `en-*` is en-US. */
const BRITISH_ENGLISH_REGIONS: ReadonlySet<string> = new Set([
  'GB',
  'IE',
  'AU',
  'NZ',
  'ZA',
  'IN',
  'SG',
  'HK',
  'MY',
  'PK',
  'NG',
  'KE',
  'GH',
  'JM',
  'TT',
  'MT',
  'CY',
]);

export interface ResolveLocaleOptions {
  /** Let `en-XA` through — non-production builds only. Default `false`. */
  allowPseudo?: boolean;
}

/** Regions that write Chinese in the traditional script; everywhere else `zh` is simplified. */
const TRADITIONAL_CHINESE_REGIONS: ReadonlySet<string> = new Set(['TW', 'HK', 'MO']);

/**
 * Best shipped locale for an ordered list of preferences (BCP 47 tags in any case; `_`
 * tolerated). `es-MX` → `es`, `pt-PT` → `pt-BR`, `en-AU` → `en-GB`, `en` → `en-US`,
 * `zh-TW` / `zh-Hant` → `zh-Hant`, any other `zh` → `zh-Hans`; anything unknown moves on to
 * the next preference, and en-US is the last resort.
 */
export function resolveLocale(
  preferences: readonly (string | null | undefined)[],
  options: ResolveLocaleOptions = {},
): AppLocale {
  const allowPseudo = options.allowPseudo ?? false;
  for (const preference of preferences) {
    const match = matchLocale(preference, allowPseudo);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

/** One preference → the pack that serves it, or `undefined` when none does. */
export function matchLocale(
  preference: string | null | undefined,
  allowPseudo = false,
): AppLocale | undefined {
  const tag = parseLanguageTag(preference);
  if (!tag) return undefined;
  switch (tag.language) {
    case 'en':
      if (tag.region === 'XA') return allowPseudo ? PSEUDO_LOCALE : undefined;
      return tag.region && BRITISH_ENGLISH_REGIONS.has(tag.region) ? 'en-GB' : 'en-US';
    case 'es':
      return 'es';
    case 'pt':
      return 'pt-BR';
    case 'zh':
      return tag.script === 'Hant' ||
        (tag.region !== undefined && TRADITIONAL_CHINESE_REGIONS.has(tag.region))
        ? 'zh-Hant'
        : 'zh-Hans';
    case 'ja':
      return 'ja';
    case 'ko':
      return 'ko';
    default:
      return undefined;
  }
}

export interface LanguageTag {
  language: string;
  region: string | undefined;
  /** Script subtag in title case (`Hant`, `Latn`), when the tag carries one. */
  script: string | undefined;
}

/** Minimal BCP 47 parse: language subtag, the first script subtag (4 letters) and the first region subtag (2 letters or 3 digits). */
export function parseLanguageTag(value: string | null | undefined): LanguageTag | undefined {
  if (typeof value !== 'string') return undefined;
  const [language = '', ...rest] = value.trim().replace(/_/g, '-').split('-');
  if (!/^[A-Za-z]{2,3}$/.test(language)) return undefined;
  const region = rest.find((subtag) => /^[A-Za-z]{2}$/.test(subtag) || /^\d{3}$/.test(subtag));
  const script = rest.find((subtag) => /^[A-Za-z]{4}$/.test(subtag));
  return {
    language: language.toLowerCase(),
    region: region?.toUpperCase(),
    script: script ? script[0]?.toUpperCase() + script.slice(1).toLowerCase() : undefined,
  };
}
